// @ts-strict
import { Injectable, ViewContainerRef } from '@angular/core';
import { LMBusyIndicatorService, LMFieldUtilService, LMFormatUtilService } from '@app/services';
import { AppConstants, LMDialogButtonKey, LMGlobalUtilService, LMUniqueIdService } from '@app/utils';
import { SohoModalDialogRef, SohoModalDialogService } from 'ids-enterprise-ng';
import { BehaviorSubject, Observable, concatMap, tap } from 'rxjs';
import { LMCreateReportSearchListDialogComponent } from '../search-list-dialog/lm-create-report-search-list-dialog.component';
import {
  LMReportPersonalizationDialogResult,
  LMReportPersonalizationDialogResultCode,
  LMReportPersonalizationDialogService,
} from '../lm-report-personalization-dialog.service';
import { LMCreateReportDialogService } from '../create-report-dialog/lm-create-report-dialog.service';
import { ReportFieldPickerConstants } from './lm-report-field-picker.constants';

type LMReportFieldPickerModel = {
  mode: LMReportPersonalizationMode;
  reportType?: LMReportType;
  businessClass?: string;
  listName?: string;
  userFolderId?: string;
  selectedFields?: LMPersonalizationColumn[];
  createReportUrl?: string;
  title?: string;
};

@Injectable({
  providedIn: 'root',
})
export class LMReportFieldPickerService {
  private isCreateReportModeSource = new BehaviorSubject<boolean>(false);
  private fieldEventTypeSource = new BehaviorSubject<'add' | 'remove'>('add');
  private selectedFieldsSource = new BehaviorSubject<Map<string, string>>(new Map());

  isCreateReportMode$ = this.isCreateReportModeSource.asObservable();
  fieldEventType$ = this.fieldEventTypeSource.asObservable();
  selectedFields$ = this.selectedFieldsSource.asObservable();

  private _isCreateReportMode = false;
  private _selectedFields = new Map();
  private _busClassName = '';

  constructor(
    private modalService: SohoModalDialogService,
    protected lmBusyIndicatorService: LMBusyIndicatorService,
    private lmUniqueIdService: LMUniqueIdService,
    private lmFormatUtilService: LMFormatUtilService,
    private lmReportPersonalizationDialogService: LMReportPersonalizationDialogService,
    private lmCreateReportDialogService: LMCreateReportDialogService,
    private lmGlobalUtilService: LMGlobalUtilService,
    private lmFieldUtilService: LMFieldUtilService
  ) {}

  set busClassName(value: string) {
    this._busClassName = value;
  }

  get isCreateReportMode() {
    return this._isCreateReportMode;
  }

  get hasUnsavedChanges() {
    return this._selectedFields.size > 0;
  }

  createReportMode(state: boolean) {
    this._isCreateReportMode = state;
    this.isCreateReportModeSource.next(state);
  }

  addField(id: string, label: string): void {
    if (!this._selectedFields.has(id)) {
      this._selectedFields.set(id, label ? label : id);
      this.onFieldEvent('add');
    }
  }

  removeField(id: string): void {
    if (this._selectedFields.delete(id)) {
      this.onFieldEvent('remove');
    }
  }

  onFieldEvent(type: 'add' | 'remove') {
    this.selectedFieldsSource.next(this._selectedFields);
    this.fieldEventTypeSource.next(type);
  }

  onAddToExistingReport() {
    const model: LMReportFieldPickerModel = {
      mode: 'createReportFromBanner',
      reportType: 'ReportCopiedFromReport',
      businessClass: this._busClassName,
    };

    this.displaySelectReportDialog(model);
  }

  onCreateNewReport() {
    const model: LMReportFieldPickerModel = {
      mode: 'createReportFromBanner',
      reportType: 'ReportFromScratch',
      businessClass: this._busClassName,
    };

    const service = this.lmCreateReportDialogService;

    service.businessClass = model.businessClass || '';

    service
      .getPrimaryList(this._busClassName)
      .pipe(
        tap((res) => (service.listName = model.listName = model.title = res?.name)),
        concatMap(() => service.loadData()),
        tap(() => (model.selectedFields = this.formatModelToDisplayFields()))
      )
      .subscribe(() => this.displayReportPersonalizationDialog(model));
  }

  // Modals
  displayUnsavedReportDialog(): Observable<LMDialogButtonKey> {
    return new Observable<LMDialogButtonKey>((observer) => {

      if (!(this.isCreateReportMode && this.hasUnsavedChanges)) {
        observer.next(LMDialogButtonKey.DISMISS);
        observer.complete();
        return;
      }

      const id = ReportFieldPickerConstants.Service.UnsavedReportDialog.dialogId;
      const title = Soho.Locale.translate(ReportFieldPickerConstants.Service.UnsavedReportDialog.title);
      const message = `<span class="message">${Soho.Locale.translate(ReportFieldPickerConstants.Service.UnsavedReportDialog.message)}</span>`;

      const attributes: SohoAttributesArray = [
        {
          name: 'data-automation-id',
          value: ReportFieldPickerConstants.Service.UnsavedReportDialog.automationid,
        },
      ];

      const options: SohoModalOptions = {
        fullsize: 'responsive',
        showCloseBtn: false,
        attributes,
      };

      const buttons: Array<SohoModalButton> = [
        {
          id: this.lmUniqueIdService.uniqueId([id, LMDialogButtonKey.CANCEL]),
          text: Soho.Locale.translate(LMDialogButtonKey.CANCEL),
          click: () => {
            dialogRef.close(LMDialogButtonKey.CANCEL);
            observer.next(LMDialogButtonKey.CANCEL);
          },
        },
        {
          id: this.lmUniqueIdService.uniqueId([id, LMDialogButtonKey.CONTINUE]),
          text: Soho.Locale.translate(LMDialogButtonKey.CONTINUE),
          click: () => {
            this.createReportMode(false);
            this._selectedFields.clear();
            dialogRef.close(LMDialogButtonKey.CONTINUE);
            observer.next(LMDialogButtonKey.CONTINUE);
          },
          isDefault: true,
        },
      ];

      const dialogRef = this.modalService
        .message(message)
        .title(title)
        .options(options)
        .buttons(buttons)
        .isAlert(true)
        .breakpoint('phablet')

        .beforeOpen(() => {
          this.lmBusyIndicatorService.showBusyIndicator();
          return true;
        })
        .afterClose((result: any) => {
          this.lmBusyIndicatorService.hideBusyIndicator();
          observer.complete();
          return true;
        })
        .open();
    });
  }

  private displaySelectReportDialog(model: LMReportFieldPickerModel) {
    let dialogRef: SohoModalDialogRef<LMCreateReportSearchListDialogComponent>;

    const id = ReportFieldPickerConstants.Service.SelectReportDialog.dialogId;
    const title = this.lmFormatUtilService.formatMessage(Soho.Locale.translate(ReportFieldPickerConstants.Service.SelectReportDialog.title), [model.businessClass]);

    const attributes: SohoAttributesArray = [
      {
        name: 'data-automation-id',
        value: ReportFieldPickerConstants.Service.SelectReportDialog.automationid,
      },
    ];

    const options: SohoModalOptions = {
      fullsize: 'responsive',
      showCloseBtn: false,
      attributes,
    };

    const buttons: Array<SohoModalButton> = [
      {
        id: this.lmUniqueIdService.uniqueId([id, LMDialogButtonKey.CANCEL]),
        text: Soho.Locale.translate(LMDialogButtonKey.CANCEL),
        click: () => {
          dialogRef.close(LMDialogButtonKey.CANCEL);
        },
      },
      {
        id: this.lmUniqueIdService.uniqueId([id, LMDialogButtonKey.OK]),
        text: Soho.Locale.translate(LMDialogButtonKey.OK),
        click: () => {
          const rows = dialogRef.componentDialog?.datagrid.selectedRows();
          if (rows?.length && rows.length > 0) {
            const selectedRow: SohoDataGridSelectedRow['data'] = rows[0]?.data;
            const service = this.lmCreateReportDialogService;

            model.listName = selectedRow?.name;
            model.userFolderId = selectedRow?.userFolderId;
            model.title = selectedRow?.title;

            service.businessClass = model.businessClass || '';
            if (model?.reportType) {
              service.reportType = model?.reportType;
            }
            service.listName = model.listName || '';
            service.userFolder = model.userFolderId || '';
            service.reportName = model.title || '';

            this.lmCreateReportDialogService
              .loadData()
              .pipe(tap((displayFields) => (model.selectedFields = this.formatModelToDisplayFields(displayFields))))
              .subscribe(() => {
                model.mode = 'personalizeReport';
                model.reportType = 'ReportAddToExisting';
                this.displayReportPersonalizationDialog(model)});
          }
          dialogRef.close();
        },
        isDefault: true,
      },
    ];

    dialogRef = this.modalService
      .modal(LMCreateReportSearchListDialogComponent)
      .title(title)
      .options(options)
      .buttons(buttons)
      .isAlert(false)
      .breakpoint('phablet')
      .apply((comp: LMCreateReportSearchListDialogComponent) => {
        comp.reportType = model.reportType;
        comp.businessClass = model.businessClass;
      })
      .beforeOpen(() => {
        this.lmBusyIndicatorService.showBusyIndicator();
        return true;
      })
      .afterClose(() => {
        this.lmBusyIndicatorService.hideBusyIndicator();
        return true;
      })
      .open();
  }

  private displayReportPersonalizationDialog(parameter: LMReportFieldPickerModel) {
    this.lmReportPersonalizationDialogService
      .runReportPersonalizationDialog(
        parameter.mode,
        <ViewContainerRef>{},
        parameter.reportType,
        parameter.businessClass,
        parameter.listName,
        parameter.userFolderId,
        parameter.selectedFields,
        parameter.createReportUrl,
        undefined,
        undefined,
        undefined,
        undefined,
        parameter.title,
        undefined,
        undefined,
        undefined,
        undefined,
        parameter.mode === 'personalizeReport',
        undefined,
        undefined,
        undefined,
        parameter.mode === 'createReportFromBanner'
      )
      .subscribe((result: LMReportPersonalizationDialogResult) => {
        if (result.status === LMReportPersonalizationDialogResultCode.REPORT_PERSONALIZATION_DIALOG_SAVED) {
          this._selectedFields.clear();
          this.createReportMode(false);

          if (result.viewReport && result.reportUrl) {
            this.lmGlobalUtilService.navigateByUrl(result.reportUrl);
          } else {
            this.lmGlobalUtilService.displayToastMessage('', result.message || '', AppConstants.TRA_ACTION_COMPLETED);
          }
        } else if (result.status === LMReportPersonalizationDialogResultCode.REPORT_PERSONALIZATION_DIALOG_CANCEL) {
          // do nothing, back to report field picker screen
        }
      });
  }

  // Utilities
  private formatModelToDisplayFields(additionalFields: LMCreateReportDisplayField[] = []): LMPersonalizationColumn[] {
    let index = Date.now();

    let displayFields: LMPersonalizationColumn[] = Array.from(
      this._selectedFields,
      ([key, value]) => (
        (index = Date.now()),
        (key = this.lmFieldUtilService.decodeFieldName(key)),
        (key = key.replace(new RegExp(':', 'g'), '.')), // convert : to .
        (key = this.isSystemField(key) ? this.parseSystemField(key) : key),
        {
          index: index,
          id: index,
          allowUpdate: false,
          canTotal: false,
          canUpdate: true,
          isValid: true,
          added: true,
          alerts: [],
          compute: false,
          defaultSort: false,
          sortDescending: false,
          sortOrder: '',
          label: value,
          name: key,
          text: key,
          value,
          type: 'Field',
          placeholder: value,
        }
      )
    );

    if (additionalFields.length) {
      displayFields = [...additionalFields, ...displayFields];
    }

    return displayFields;
  }

  private isSystemField(id: string): boolean {
    return [ 'effective_date', 'reason_code', 'action_comment' ].some(sf => `${this._busClassName}_${sf}` == id);
  }

  private parseSystemField(id: string): string {
    const idArr = id?.split('_') || [];
    idArr.shift();
    return idArr.join(' ');
  }
}
